import { inject } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { Combine, Restriction } from '../../../api/entities/api-query.entity';
import { User } from '../../../users/entities/user.entity';
import { UserService } from '../../../users/services/user.service';
import { ZonesService } from '../../../zones/services/zones.service';
import { RecordPhase, RecordType } from '../../entities/metadata/metadata.entity';
import { RestrictionOptions } from '../../entities/restrictions/restriction-options.entity';
import { RestrictionType } from '../../entities/restrictions/restriction-types.entity';
import { RecordRestrictions } from '../../helpers/restrictions/record-restrictions.helper';
import { ZoneRestrictions } from '../../helpers/restrictions/record-zone-restrictions.helper';

/**
 * Sets the default Restrictions for search
 * This way we can easily limit the search results within a route.
 *
 * At the moment this is only used for the autocomplete.
 *
 */
export class ShRecordRestrictionService {
    private restrictionOptions: RestrictionOptions = {
        type: RestrictionType.All,
        parentId: null,
        publicOnly: false
    };

    private readonly zoneRestrictions: ZoneRestrictions = inject(ZoneRestrictions);
    private readonly zoneService: ZonesService = inject(ZonesService);
    private readonly userService: UserService = inject(UserService);

    public initialize(options: RestrictionOptions): Observable<Restriction> {
        this.restrictionOptions = options;
        return this.getRestrictions();
    }

    public getRestrictions(options: RestrictionOptions = this.restrictionOptions): Observable<Restriction> {
        return combineLatest([this.getRestrictionsOfType(options.type), this.userService.getCurrentUser()]).pipe(
            map(([restriction, user]) => this.addAdditionalRestrictions(restriction, options, user))
        );
    }

    // #region Restrictions of type
    public getRestrictionsOfType(type: RestrictionType): Observable<Restriction> {
        switch (type) {
            case RestrictionType.All:
                return this.getRestrictionsAll();
            case RestrictionType.Concepts:
                return this.getRestrictionsConcepts();
            case RestrictionType.Published:
                return this.getRestrictionsPublished();
            case RestrictionType.PublishedCollections:
                return this.getRestrictionsPublishedCollections();
            case RestrictionType.Archived:
                return this.getRestrictionsArchived();
            case RestrictionType.Deleted:
                return this.getRestrictionsDeleted();
            default:
                return this.getRestrictionsAll();
        }
    }

    // MediaHaven All-objects page
    private getRestrictionsAll(): Observable<Restriction> {
        return this.zoneService.zones$.pipe(
            map(zones =>
                Combine.And(
                    this.zoneRestrictions.regularRecordTypesZone(),
                    Combine.Or(
                        RecordRestrictions.phaseRestriction([RecordPhase.Published, RecordPhase.Archived]),
                        RecordRestrictions.conceptZoneRestriction(zones)
                    )
                )
            )
        );
    }

    // MediaHaven Concepts page
    private getRestrictionsConcepts(): Observable<Restriction> {
        return this.zoneService.zones$.pipe(
            map(zones =>
                Combine.And(
                    this.zoneRestrictions.regularRecordTypesZone(),
                    RecordRestrictions.conceptZoneRestriction(zones)
                )
            )
        );
    }

    // Mediahaven Published and Portal Media page
    private getRestrictionsPublished(): Observable<Restriction> {
        return of(
            Combine.And(this.zoneRestrictions.regularRecordTypesZone(), RecordRestrictions.publishedPhaseRestriction())
        );
    }

    // Mediahaven Archived page
    private getRestrictionsArchived(): Observable<Restriction> {
        return of(
            Combine.And(this.zoneRestrictions.regularRecordTypesZone(), RecordRestrictions.archivedPhaseRestriction())
        );
    }

    // Portal Collections page
    private getRestrictionsPublishedCollections(): Observable<Restriction> {
        return of(
            Combine.And(
                this.zoneRestrictions.regularRecordTypesZone(),
                RecordRestrictions.publishedPhaseRestriction(),
                RecordRestrictions.includeRecordTypeRestriction(RecordType.Mh2Collection)
            )
        );
    }

    // Recycle page
    private getRestrictionsDeleted(): Observable<Restriction> {
        return this.zoneService.zones$.pipe(map(zones => this.zoneRestrictions.deletedRecordTypesZone(zones)));
    }
    // #endregion

    // #region Additional Restrictions
    private addAdditionalRestrictions(restriction: Restriction, options: RestrictionOptions, user: User): Restriction {
        const additionalRestrictions: Restriction[] = [];

        if (options.parentId) {
            additionalRestrictions.push(RecordRestrictions.childOfParentRestriction(options.parentId));
        }

        if (options.publicOnly) {
            additionalRestrictions.push(RecordRestrictions.publicRestriction(user.Groups));
        }

        if (additionalRestrictions.length == 0) {
            return restriction;
        }

        return Combine.And(restriction, ...additionalRestrictions);
    }
    // #endregion
}
